<template>
  <div
    class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg shadow-black/30 backdrop-blur"
    :class="panelClass">
    <div class="p-4">
      <div class="flex items-start">
        <div class="shrink-0">
          <span class="inline-flex size-8 items-center justify-center rounded-md border" :class="iconContainerClass">
            <component :is="iconComponent" class="size-5" :class="iconClass" />
          </span>
        </div>

        <div class="ml-3 w-0 flex-1 pt-0.5">
          <p v-if="toast.title" class="text-sm font-semibold text-white">{{ toast.title }}</p>
          <p v-if="toast.message" class="mt-1 text-sm text-gray-300">{{ toast.message }}</p>

          <div v-if="hasActions" class="mt-3 flex flex-wrap items-center gap-2">
            <AppButton
              v-for="(action, index) in toast.actions"
              :key="`${toast.id}-action-${index}`"
              type="button"
              :variant="actionVariant(action)"
              :size="actionSize(action)"
              @click="$emit('action', action)">
              {{ action.label }}
            </AppButton>
          </div>
        </div>

        <div v-if="toast.dismissible" class="ml-3 flex shrink-0">
          <AppButton
            type="button"
            variant="tertiary"
            size="sm"
            icon-only
            @click="$emit('close')">
            <span class="sr-only">{{ $t('common.actions.close') }}</span>
            <XMarkIcon class="size-5" />
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'

const TOAST_VARIANTS = {
  success: {
    panelClass: 'border-emerald-500/25 bg-gray-900/95',
    iconContainerClass: 'border-emerald-500/35 bg-emerald-500/12',
    iconClass: 'text-emerald-300',
    icon: CheckCircleIcon,
  },
  error: {
    panelClass: 'border-rose-500/25 bg-gray-900/95',
    iconContainerClass: 'border-rose-500/35 bg-rose-500/12',
    iconClass: 'text-rose-300',
    icon: ExclamationCircleIcon,
  },
  warning: {
    panelClass: 'border-amber-500/25 bg-gray-900/95',
    iconContainerClass: 'border-amber-500/35 bg-amber-500/12',
    iconClass: 'text-amber-300',
    icon: ExclamationTriangleIcon,
  },
  info: {
    panelClass: 'border-sky-500/25 bg-gray-900/95',
    iconContainerClass: 'border-sky-500/35 bg-sky-500/12',
    iconClass: 'text-sky-300',
    icon: InformationCircleIcon,
  },
  action: {
    panelClass: 'border-indigo-500/25 bg-gray-900/95',
    iconContainerClass: 'border-indigo-500/35 bg-indigo-500/12',
    iconClass: 'text-indigo-300',
    icon: InformationCircleIcon,
  },
}

export default {
  components: {
    XMarkIcon,
  },
  emits: ['close', 'action'],
  props: {
    toast: {
      type: Object,
      required: true,
    },
  },
  computed: {
    variantConfig() {
      return TOAST_VARIANTS[this.toast.variant] ?? TOAST_VARIANTS.info
    },
    panelClass() {
      return this.variantConfig.panelClass
    },
    iconContainerClass() {
      return this.variantConfig.iconContainerClass
    },
    iconClass() {
      return this.variantConfig.iconClass
    },
    iconComponent() {
      return this.variantConfig.icon
    },
    hasActions() {
      return Array.isArray(this.toast.actions) && this.toast.actions.length > 0
    },
  },
  methods: {
    actionVariant(action) {
      const variant = action?.variant ?? 'tertiary'

      if (variant === 'primary' || variant === 'secondary' || variant === 'danger') {
        return variant
      }

      return 'tertiary'
    },
    actionSize(action) {
      const size = action?.size ?? 'xs'

      if (size === 'xs' || size === 'sm' || size === 'md' || size === 'lg' || size === 'xl') {
        return size
      }

      return 'xs'
    },
  },
}
</script>
